import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { formatRupiah, formatDay } from "../../lib/labels";
import { Input } from "../../components/ui/input";

const STATUS = {
  RECORDED: ["Dicatat (PT SJI pembeli)", "bg-blue-50 text-blue-800"],
  TRANSFERRED: ["Dibayar", "bg-emerald-50 text-emerald-800"],
  REPORTED: ["Dibayar (dilaporkan Pengelola)", "bg-emerald-50 text-emerald-800"],
  PENDING: ["Belum dibayar", "bg-amber-50 text-amber-800"],
};
const thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };

const Card = ({ label, value, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
    <p className="mt-1 text-lg font-bold font-mono text-slate-900" data-testid={testid}>{formatRupiah(value || 0)}</p>
  </div>
);

const Items = ({ items }) => (
  <div className="overflow-x-auto"><table className="w-full text-xs min-w-[640px]">
    <thead className="text-left uppercase text-slate-500"><tr><th className="py-2 pr-2">Tgl Penjualan</th><th>Pembeli</th><th>Hasil Bersih</th><th>Imbalan Jasa</th><th>Status</th><th>Tgl Bayar / Ref</th></tr></thead>
    <tbody>{items.map((i) => (
      <tr key={i.payoutId} className="border-t border-slate-100" data-testid={`recap-item-${i.payoutId}`}>
        <td className="py-2 pr-2">{formatDay(i.saleDate)}</td>
        <td>{i.buyerType === "PT_SJI" ? "PT SJI (pembeli)" : i.buyerName || "Pembeli lain"}</td>
        <td className="font-mono">{formatRupiah(i.netAmount || 0)}</td>
        <td className="font-mono font-semibold">{formatRupiah(i.amount)}</td>
        <td><span className={`rounded px-1.5 py-0.5 ${(STATUS[i.status] || [])[1]}`}>{(STATUS[i.status] || [i.status])[0]}</span></td>
        <td>{i.transferDate || i.doneAt ? formatDay(i.transferDate || i.doneAt) : "-"}{i.reference ? ` · ${i.reference}` : ""}</td>
      </tr>))}</tbody>
  </table></div>
);

const GardenRow = ({ g }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-t border-slate-200 cursor-pointer hover:bg-slate-50" onClick={() => setOpen(!open)} data-testid={`recap-garden-${g.projectId}`}>
        <td className="py-3 pr-2">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
        <td className="font-semibold"><Link to={`/dashboard/admin/bagi-hasil/${g.contractId}`} onClick={(e) => e.stopPropagation()} className="hover:underline" data-testid={`recap-garden-link-${g.projectId}`}>{g.projectCode || "-"}</Link><p className="text-xs font-normal text-slate-500">{g.name}</p></td>
        <td className="font-mono">{formatRupiah(g.recorded)}</td>
        <td className="font-mono text-emerald-800">{formatRupiah(g.paid)}</td>
        <td className="font-mono text-amber-800">{formatRupiah(g.pending)}</td>
        <td className="font-mono font-bold">{formatRupiah(g.total)}</td>
      </tr>
      {open && <tr><td colSpan={6} className="bg-slate-50 px-3 pb-3"><Items items={g.items} /></td></tr>}
    </>
  );
};

export default function AdminImbalanRecap() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setErr("");
    api.get("/profit/imbalan-recap", { params: { month } }).then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));
  }, [month]);
  const t = data?.totals || {};
  return (
    <div className="space-y-6" data-testid="imbalan-recap-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Rekap Imbalan Jasa PT SJI</h1>
          <p className="text-sm text-slate-600">Per kebun, berdasarkan tanggal penjualan panen. Skema final: Bagian PT SJI 2,5% sebelum BEP dan 5% sesudah BEP, termasuk dalam pembagian 100% laba kebun (perjanjian lama mengikuti ketentuannya).</p>
        </div>
        <label className="text-xs text-slate-600">Bulan<Input type="month" className="mt-1 w-44" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="recap-month-input" /></label>
      </div>
      {err && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-800" data-testid="recap-error">{err}</p>}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label="Total Imbalan Jasa" value={t.total} testid="recap-total" />
        <Card label="Dicatat (PT SJI pembeli)" value={t.recorded} testid="recap-recorded" />
        <Card label="Sudah dibayar" value={t.paid} testid="recap-paid" />
        <Card label="Belum dibayar" value={t.pending} testid="recap-pending" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4 overflow-x-auto">
        {data && data.gardens.length === 0 ? <p className="text-sm text-slate-500" data-testid="recap-empty">Belum ada Imbalan Jasa PT SJI pada bulan ini.</p> : (
          <table className="w-full text-sm min-w-[600px]" data-testid="recap-table">
            <thead className="text-left text-xs uppercase text-slate-500"><tr><th className="w-6"></th><th>Kebun</th><th>Dicatat</th><th>Dibayar</th><th>Belum dibayar</th><th>Total</th></tr></thead>
            <tbody>{(data?.gardens || []).map((g) => <GardenRow key={g.projectId} g={g} />)}</tbody>
          </table>)}
      </div>
      <p className="text-xs text-slate-500">"Dicatat" = penjualan ke PT SJI sebagai pembeli; imbalan jasa dicatat terpisah dari harga beli. "Dibayar" = penjualan ke pembeli lain yang pembayarannya sudah dilaporkan atau ditransfer.</p>
    </div>
  );
}
